import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { FileText, Plus, Eye, Trash2, Edit3, LogOut, Briefcase, Settings, HelpCircle, BookOpen } from "lucide-react";
import { toast } from "sonner";

export default function Dashboard() {
  const navigate = useNavigate();
  const [requisitions, setRequisitions] = useState([]);
  const [stats, setStats] = useState({ total: 0, recent: 0 });
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteReqId, setDeleteReqId] = useState(null);

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    toast.success("Logged out successfully");
    navigate("/");
  };

  useEffect(() => {
    fetchRequisitions();
  }, []);

  const fetchRequisitions = async () => {
    try {
      const response = await apiClient.get("/requisitions");
      const reqs = response.data;
      
      // Sort by updated_at descending (most recent first)
      const sorted = [...reqs].sort((a, b) => {
        const dateA = new Date(a.updated_at || a.created_at);
        const dateB = new Date(b.updated_at || b.created_at);
        return dateB - dateA;
      });
      
      setRequisitions(sorted);
      
      // Calculate stats
      const now = new Date();
      const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      const recent = reqs.filter(req => {
        const createdDate = new Date(req.created_at);
        return createdDate >= sevenDaysAgo;
      });
      
      setStats({
        total: reqs.length,
        recent: recent.length,
      });
    } catch (error) {
      console.error("Failed to fetch requisitions:", error);
      toast.error("Failed to load requisitions");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this requisition?")) {
      return;
    }

    setIsDeleting(true);
    setDeleteReqId(id);
    try {
      await apiClient.delete(`/requisitions/${id}`);
      toast.success("Requisition deleted successfully");
      fetchRequisitions();
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Failed to delete requisition");
    } finally {
      setIsDeleting(false);
      setDeleteReqId(null);
    }
  };

  const handleView = (id) => {
    navigate(`/requisition/${id}`);
  };

  const handleEdit = (id) => {
    navigate(`/builder?edit=${id}`);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header with Logout */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Briefcase className="w-8 h-8 text-blue-900" />
            <div>
              <h1 className="text-xl font-bold text-slate-900">SmartReqs</h1>
              <p className="text-xs text-slate-600">CaioAbruzzo LLC</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate("/guide")}
              data-testid="guide-btn"
            >
              <BookOpen className="w-4 h-4 mr-2" />
              Guide
            </Button>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate("/faq")}
              data-testid="faq-btn"
            >
              <HelpCircle className="w-4 h-4 mr-2" />
              FAQ
            </Button>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate("/settings")}
              data-testid="settings-btn"
            >
              <Settings className="w-4 h-4 mr-2" />
              Settings
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Stats Cards */}
      <div className="max-w-6xl mx-auto px-6 py-8">
        {stats.total > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            <Card className="bg-gradient-to-br from-blue-50 to-white border-blue-200">
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                    <FileText className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-3xl font-bold text-blue-900">{stats.total}</p>
                    <p className="text-sm text-blue-700">Total Requisitions</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Actions and Image */}
        <div className="flex items-start justify-between mb-6 gap-6">
          <div className="flex-1">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">Requisition Desktop</h2>
            <div className="flex gap-3">
              <Button onClick={() => navigate("/builder")} data-testid="create-requisition-btn">
                <Plus className="w-4 h-4 mr-2" />
                Create Requisition
              </Button>
              <Button variant="outline" onClick={() => navigate("/library")}>
                View Req Library
              </Button>
            </div>
          </div>
          
          {/* Image in upper right */}
          <div className="flex-shrink-0">
            <img
              src="/req-desktop-pic.jpg"
              alt="Requisition Desktop"
              className="w-[512px] h-auto rounded-lg shadow-md object-cover"
            />
          </div>
        </div>

        {/* Requisitions List */}
        {requisitions.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">No Requisitions Yet</h3>
              <p className="text-slate-600 mb-4">Create your first job requisition to get started</p>
              <Button onClick={() => navigate("/builder")}>
                <Plus className="w-4 h-4 mr-2" />
                Create Requisition
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {requisitions.map((req) => (
              <Card key={req.id} className="hover:shadow-md transition-shadow" data-testid="requisition-card">
                <CardHeader>
                  <div className="space-y-3">
                    <div>
                      <CardTitle className="text-lg text-slate-900 mb-1" data-testid="requisition-name">{req.name}</CardTitle>
                      <p className="text-sm text-slate-600 mb-2" data-testid="requisition-title">{req.title}</p>
                      
                      {/* Summary Information */}
                      <div className="space-y-1 mb-2">
                        {req.location && (
                          <p className="text-xs text-slate-600">
                            <span className="font-semibold">Location:</span> {req.location}
                          </p>
                        )}
                        {req.experience_years && (
                          <p className="text-xs text-slate-600">
                            <span className="font-semibold">Experience:</span> {req.experience_years}
                          </p>
                        )}
                        {req.salary_range && (
                          <p className="text-xs text-slate-600">
                            <span className="font-semibold">Salary:</span> {req.salary_range}
                          </p>
                        )}
                        {req.description && (
                          <p className="text-xs text-slate-600 line-clamp-2">
                            {req.description}
                          </p>
                        )}
                      </div>
                      
                      <div className="flex gap-4 mt-2 text-xs text-slate-500">
                        <span>Created: {formatDate(req.created_at)}</span>
                        {req.updated_at && req.updated_at !== req.created_at && (
                          <span>Updated: {formatDate(req.updated_at)}</span>
                        )}
                      </div>
                    </div>
                    
                    {/* Action Buttons */}
                    <div className="flex gap-2 pt-2 border-t border-slate-200">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleView(req.id)}
                        data-testid="view-requisition-btn"
                        className="flex-1"
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        View
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEdit(req.id)}
                        data-testid="edit-requisition-btn"
                        className="flex-1"
                      >
                        <Edit3 className="w-4 h-4 mr-1" />
                        Edit Input
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDelete(req.id)}
                        disabled={isDeleting && deleteReqId === req.id}
                        data-testid="delete-requisition-btn"
                        className="flex-1"
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        {isDeleting && deleteReqId === req.id ? "Deleting..." : "Delete"}
                      </Button>
                    </div>
                  </div>
                </CardHeader>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
